import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { ItemAdminType, ProductRelation } from "../../types/types";
import { useGetItemsQuery } from "../../services/items/items";
import { RELATED_PRODUCTS_TEXTS, RELATION_LABELS } from "../shared/constants";
import { isAlreadyRelated } from "../../utils/relations";

interface RelatedProductsEditorProps {
  relations: ProductRelation[];
  onChange: (relations: ProductRelation[]) => void;
}

const RELATION_TYPES = Object.keys(RELATION_LABELS) as ProductRelation["tipo_relacion"][];

/**
 * How many options may reach the DOM at once (EB-511).
 *
 * MUI's `Autocomplete` has no virtualization: it renders every option that survives filtering, so
 * the listbox grew linearly with the catalog — ~300 nodes before the Alegra sync, ~2,133 after.
 *
 * `limit` was chosen over a virtualized `ListboxComponent`: virtualization means a new runtime
 * dependency (`react-window`) and a custom listbox, and MUI itself only recommends it past ~5,000
 * options. This is a search-driven picker — nobody scrolls 2,133 products looking for one — so
 * capping the rendered slice solves the same problem in a few lines. Revisit if the catalog grows
 * far enough that 50 alphabetical matches stop being a useful starting point.
 */
export const RELATED_OPTIONS_LIMIT = 50;

const optionLabel = (option: ItemAdminType) => option.name ?? option.id ?? "";

// Matching runs across the whole catalog; only the slice handed to the DOM is capped. Searching by
// id as well as name matters now that most of the catalog is `alg-####` rows with terse names.
export const filterOptions = createFilterOptions<ItemAdminType>({
  limit: RELATED_OPTIONS_LIMIT,
  stringify: (option) => `${option.name ?? ""} ${option.id ?? ""}`,
});

const RelatedProductsEditor: React.FC<RelatedProductsEditorProps> = ({ relations, onChange }) => {
  const { data: allItems } = useGetItemsQuery();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ItemAdminType | null>(null);
  const [selectedRelationType, setSelectedRelationType] =
    useState<ProductRelation["tipo_relacion"]>("repuesto");

  const existingIds = useMemo(() => new Set(relations.map((r) => r.id)), [relations]);

  const availableProducts = useMemo(
    () => (allItems ?? []).filter((item) => item.id && !existingIds.has(item.id)),
    [allItems, existingIds],
  );

  const itemNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (allItems ?? []).forEach((item) => {
      if (item.id) map.set(item.id, item.name);
    });
    return map;
  }, [allItems]);

  const handleAdd = () => {
    if (!selectedProduct?.id) return;

    // Refuse a repeat rather than appending it (EB-522). The Autocomplete already excludes related
    // products, so this is unreachable through the happy path — which is exactly why 17 duplicates
    // reached production unnoticed. Nothing in the code stopped them.
    if (isAlreadyRelated(relations, selectedProduct.id)) {
      setSelectedProduct(null);
      setDialogOpen(false);
      return;
    }

    onChange([...relations, { id: selectedProduct.id, tipo_relacion: selectedRelationType }]);
    setSelectedProduct(null);
    setSelectedRelationType("repuesto");
    setDialogOpen(false);
  };

  const handleRemove = (idx: number) => {
    onChange(relations.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {relations.length > 0 ? (
        <Box display="flex" flexDirection="column" gap={0.5}>
          {relations.map((rel, idx) => (
            <Box key={`${rel.id}-${idx}`} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {itemNameMap.get(rel.id) ?? rel.id}
              </Typography>
              <Chip label={RELATION_LABELS[rel.tipo_relacion] ?? rel.tipo_relacion} size="small" />
              <IconButton size="small" color="error" onClick={() => handleRemove(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No hay productos relacionados.
        </Typography>
      )}

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        onClick={() => setDialogOpen(true)}
        sx={{ mt: 1, fontWeight: "bold" }}
      >
        Agregar relaci&oacute;n
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar producto relacionado</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Autocomplete
            options={availableProducts}
            getOptionLabel={optionLabel}
            // Caps how many options reach the DOM. MUI's Autocomplete renders every option that
            // survives filtering, with no virtualization of its own, so a 2,133-product catalog
            // built 2,133 <li> nodes on each keystroke (EB-511). Filtering still runs over the
            // whole list — only the rendered slice is capped.
            filterOptions={filterOptions}
            value={selectedProduct}
            onChange={(_, value) => setSelectedProduct(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={RELATED_PRODUCTS_TEXTS.searchLabel}
                size="small"
                sx={{ mt: 1 }}
                helperText={RELATED_PRODUCTS_TEXTS.searchHelper(
                  availableProducts.length,
                  RELATED_OPTIONS_LIMIT,
                )}
              />
            )}
            noOptionsText={RELATED_PRODUCTS_TEXTS.noOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de relaci&oacute;n</InputLabel>
            <Select
              value={selectedRelationType}
              onChange={(e) =>
                setSelectedRelationType(e.target.value as ProductRelation["tipo_relacion"])
              }
              label="Tipo de relación"
            >
              {RELATION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {RELATION_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained" disabled={!selectedProduct}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelatedProductsEditor;
